import '@hotmart/cosmos/dist/date-picker'
import { forwardRef, useCallback, useEffect, useMemo } from 'react'

import { Languages } from '@space-shell/constant/languages'
import { Spanish } from 'flatpickr/dist/l10n/es'
import { Portuguese } from 'flatpickr/dist/l10n/pt'

import { useTranslation } from 'react-i18next'

import { useDatePickerRef } from './hooks'
import { datePickerModal } from './styles'
import type { DatePickerProps, HotDatePickerProps } from './types'

const MAX_DATE_NUMBER = 8640000000000000
const MAX_DATE = new Date(MAX_DATE_NUMBER)
const DATE_REGEX =
  /^(?:(?:31\/(?:0?[13578]|1[02]))\/|(?:(?:29|30)\/(?:0?[13-9]|1[0-2])\/))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29\/0?2\/(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])\/(?:0?[1-9]|1[0-2])\/(?:(?:1[6-9]|[2-9]\d)?\d{2})$/

const DatePicker = forwardRef<JSX.IntrinsicElements['hot-date-picker'], DatePickerProps>(
  (
    { onChange, placeholder, invalid, value, locale = Languages.Pt, dateFormat = 'DD/MM/YYYY' },
    forwardedRef
  ) => {
    const { ref } = useDatePickerRef(forwardedRef)
    const changeHandler = useCallback((event: Event) => onChange(event as CustomEvent), [onChange])
    const { t } = useTranslation()

    const customPickerProps = useMemo(
      () => ({
        invalid: invalid || undefined,
        placeholder: DATE_REGEX.test(placeholder || '')
          ? placeholder
          : t('configuration_sidebar.members_and_permissions.list.expiration_date_undefined'),
        date: DATE_REGEX.test(value || '') ? value : null,
        dateFormat
      }),
      [invalid, placeholder, value, dateFormat, t]
    )

    const l10n = useMemo(() => {
      return {
        'start-date-placeholder': t(
          'configuration_sidebar.members_and_permissions.list.expiration_date_placeholder'
        ),
        cancel: t('buttons.cancel'),
        apply: t('configuration_sidebar.members_and_permissions.list.expiration_date_apply')
      }
    }, [t])

    useEffect(() => {
      const hotDatePicker = ref.current

      if (!hotDatePicker) return
      ;(hotDatePicker as HotDatePickerProps).addEventListener('change', changeHandler)

      return () => {
        hotDatePicker.removeEventListener('change', changeHandler)
      }
    }, [onChange, changeHandler, ref])

    useEffect(() => {
      const hotDatePicker = ref.current

      const treatedLocale = locale.toLowerCase()

      if (!hotDatePicker) {
        return
      }

      if (treatedLocale === Languages.Pt || treatedLocale === 'pt_br') {
        hotDatePicker?.setLocale(Portuguese, l10n)
      } else if (treatedLocale === Languages.Es) {
        hotDatePicker?.setLocale(Spanish, l10n)
      }

      const currentDate = new Date()
      currentDate.setDate(currentDate.getDate() - 1)

      hotDatePicker?.setEnabledDates([{ from: currentDate, to: MAX_DATE }])
    }, [l10n, locale, ref])

    useEffect(() => {
      const elements = document?.querySelectorAll('hot-date-picker-concrete')

      const datePickerModalStyles = document.createElement('style')
      datePickerModalStyles.prepend(datePickerModal.toString())

      if (elements.length > 1) {
        elements.forEach((element) => {
          element.shadowRoot?.prepend(datePickerModalStyles)
        })
      } else {
        document
          ?.querySelector('hot-date-picker-concrete')
          ?.shadowRoot?.prepend(datePickerModalStyles)
      }
    }, [])

    return (
      <hot-date-picker {...customPickerProps} ref={ref} class='_bg-transparent'></hot-date-picker>
    )
  }
)

export default DatePicker
